import React, { useState, useMemo, useRef } from "react";
import Pagination from "./Pagination";
import { CaretDown, CaretUp, formatNumber, UnfoldMore } from "../normalizer";

/**
 * Sort indicator icon
 */
const SortIcon = ({ direction, active, className }) => {
  if (!active) {
    return (
      <UnfoldMore className={`data-table__sort-icon ${className}__sort-icon`} />
    );
  }
  return direction === "asc" ? (
    <CaretUp
      className={`data-table__sort-icon data-table__sort-icon--active ${className}__sort-icon ${className}__sort-icon--active`}
    />
  ) : (
    <CaretDown
      className={`data-table__sort-icon data-table__sort-icon--active ${className}__sort-icon ${className}__sort-icon--active`}
    />
  );
};

/**
 * Table header with sort functionality
 */
const SortableHeader = ({
  label,
  sortKey,
  currentSort,
  onSort,
  align = "left",
  className,
  sortable = true,
}) => {
  const isActive = currentSort.key === sortKey;
  const alignClass =
    align === "center"
      ? `data-table__header--center ${className}__header--center`
      : align === "right"
      ? `data-table__header--right ${className}__header--right`
      : "";

  if (!sortable) {
    return (
      <th
        className={`data-table__header data-table__cell--${sortKey} ${className}__header ${alignClass} ${className}__cell--${sortKey}`}
      >
        {label}
      </th>
    );
  }

  return (
    <th
      className={`data-table__header data-table__cell--${sortKey} ${className}__header ${alignClass} ${className}__cell--${sortKey}`}
      onClick={() => onSort(sortKey)}
    >
      <span
        className={`data-table__header-content ${className}__header-content`}
      >
        {label}
        <SortIcon
          direction={currentSort.direction}
          active={isActive}
          className={className}
        />
      </span>
    </th>
  );
};

/**
 * Generic CustomDataTable component
 *
 * @param {string} className - Base CSS class name (e.g., "ranking-table", "balance-table")
 * @param {Array} columns - Column definitions: [{ key, label, sortKey, align, sortable, render, totals, totalsLabel, totalsFormatter, hideOnMobile, mobileFullWidth, mobileLabel }]
 * @param {Array} data - Array of data rows
 * @param {Function} renderRow - Custom row renderer: (item, index, className) => JSX
 * @param {Function} renderCard - Optional override for mobile card renderer: (item, index, className) => JSX
 * @param {Function} rowKey - Key extractor: (item, index) => string/number
 * @param {Function} rowClassName - Optional row class generator: (item, index) => string
 * @param {Object} defaultSort - Initial sort state: { key, direction }
 * @param {Function} onSort - Optional external sort handler
 * @param {Function} sortFn - Custom sort function: (data, sort) => sortedData
 * @param {Object} pagination - { totalPages, currentPage, onPageChange }
 * @param {string} emptyMessage - Message when no data
 * @param {boolean} forceCards - Force mobile card layout regardless of column count
 */
const CustomDataTable = ({
  className = "data-table",
  columns = [],
  data = [],
  renderRow,
  renderCard,
  rowKey = (item, index) => item.id || index,
  rowClassName,
  defaultSort = { key: null, direction: "asc" },
  onSort,
  sortFn,
  pagination,
  emptyMessage = "No data available",
  forceCards = false,
}) => {
  const rootRef = useRef(null);
  const [sort, setSort] = useState(defaultSort);

  const handlePageChange = (page) => {
    if (rootRef.current && window.innerWidth < 740) {
      const navbar = document.getElementById("g-navbar");
      const navbarHeight = navbar ? navbar.offsetHeight : 0;
      const top = rootRef.current.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
      window.scrollTo({ top, behavior: "smooth" });
    }
    if (pagination && pagination.onPageChange) {
      pagination.onPageChange(page);
    }
  };

  const handleSort = (key) => {
    const newSort = {
      key,
      direction: sort.key === key && sort.direction === "asc" ? "desc" : "asc",
    };
    setSort(newSort);
    if (onSort) {
      onSort(newSort);
    }
  };

  // Sort data using custom sortFn or return unsorted
  const sortedData = useMemo(() => {
    if (sortFn && sort.key) {
      return sortFn(data, sort);
    }
    return data;
  }, [data, sort, sortFn]);

  // Default row renderer if none provided
  const defaultRenderRow = (item, index) => {
    const rowClasses = rowClassName
      ? `data-table__row ${className}__row ${rowClassName(item, index)}`
      : `data-table__row ${className}__row`;

    return (
      <tr key={rowKey(item, index)} className={rowClasses}>
        {columns.map((col) => (
          <td
            key={col.key}
            className={`data-table__cell data-table__cell--${col.key} ${className}__cell ${className}__cell--${col.key}`}
          >
            {col.render
              ? col.render(item[col.key], item, index)
              : item[col.key]}
          </td>
        ))}
      </tr>
    );
  };

  const rowRenderer = renderRow || defaultRenderRow;

  // ── Mobile card renderer ──
  const getCellValue = (col, item, index) =>
    col.render ? col.render(item[col.key], item, index) : item[col.key];

  const defaultRenderCard = (item, index) => {
    const mainCols = [];
    const actionCols = [];
    columns.forEach((col) => {
      if (col.hideOnMobile) return;
      if (col.mobileFullWidth || col.key === "actions") {
        actionCols.push(col);
      } else {
        mainCols.push(col);
      }
    });

    const extraClass = rowClassName ? rowClassName(item, index) : "";

    return (
      <div
        key={rowKey(item, index)}
        className={`data-table__card ${className}__card ${extraClass}`}
      >
        <div className={`data-table__card-grid ${className}__card-grid`}>
          {mainCols.map((col) => (
            <div
              key={col.key}
              className={`data-table__card-cell data-table__card-cell--${col.key} ${className}__card-cell ${className}__card-cell--${col.key}`}
            >
              <span className={`data-table__card-label ${className}__card-label`}>
                {col.mobileLabel !== undefined ? col.mobileLabel : col.label}
              </span>
              <span className={`data-table__card-value ${className}__card-value`}>
                {getCellValue(col, item, index)}
              </span>
            </div>
          ))}
        </div>
        {actionCols.length > 0 && (
          <div className={`data-table__card-actions ${className}__card-actions`}>
            {actionCols.map((col) => (
              <div
                key={col.key}
                className={`data-table__card-action data-table__card-action--${col.key} ${className}__card-action ${className}__card-action--${col.key}`}
              >
                {getCellValue(col, item, index)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const cardRenderer = renderCard || defaultRenderCard;

  // ── Totals row logic ──
  const hasTotals = columns.some((col) => col.totals);
  const totals = useMemo(() => {
    if (!hasTotals || data.length === 0) return null;
    const result = {};
    columns.forEach((col) => {
      if (col.totals) {
        result[col.key] = data.reduce((sum, item) => {
          const val = parseFloat(item[col.key]);
          return sum + (isNaN(val) ? 0 : val);
        }, 0);
      }
    });
    return result;
  }, [data, columns, hasTotals]);

  // Cards are rendered for tables with more than 3 visible columns, or when
  // forceCards=true is passed explicitly (e.g. balance-table with 3 cols).
  const visibleColumnCount = columns.filter((c) => !c.hideOnMobile).length;
  const useMobileCards = forceCards || visibleColumnCount > 3;
  const rootModifier = useMobileCards ? "" : "data-table--no-cards";

  return (
    <div ref={rootRef} className={`${className} ${rootModifier}`}>
      <div className={`data-table__container ${className}__container`}>
        <table className={`data-table__table ${className}__table`}>
          <thead>
            <tr
              className={`data-table__row data-table__row__header ${className}__row ${className}__row__header`}
            >
              {columns.map((col) => (
                <SortableHeader
                  key={col.key}
                  label={col.label}
                  sortKey={col.sortKey || col.key}
                  currentSort={sort}
                  onSort={handleSort}
                  align={col.align}
                  className={className}
                  sortable={col.sortable !== false}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr
                className={`data-table__row data-table__row--empty ${className}__row ${className}__row--empty`}
              >
                <td
                  colSpan={columns.length}
                  className={`data-table__cell ${className}__cell`}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedData.map((item, index) =>
                rowRenderer(item, index, className),
              )
            )}
          </tbody>
          {totals && data.length > 0 && (
            <tfoot>
              <tr className={`data-table__row data-table__row--totals ${className}__row ${className}__row--totals`}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`data-table__cell data-table__cell--totals data-table__cell--${col.key} ${className}__cell ${className}__cell--totals ${className}__cell--${col.key}`}
                  >
                    {col.totals && totals[col.key] !== undefined
                      ? (col.totalsFormatter
                          ? col.totalsFormatter(totals[col.key])
                          : formatNumber(totals[col.key]))
                      : (col.totalsLabel || '')}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {useMobileCards && (
        <div className={`data-table__cards ${className}__cards`}>
          {sortedData.length === 0 ? (
            <div
              className={`data-table__card data-table__card--empty ${className}__card ${className}__card--empty`}
            >
              {emptyMessage}
            </div>
          ) : (
            sortedData.map((item, index) => cardRenderer(item, index, className))
          )}
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className={`data-table__pagination ${className}__pagination`}>
          <Pagination
            totalPages={pagination.totalPages}
            currentPage={pagination.currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default CustomDataTable;
